import React, { useEffect, useState } from "react";
import { Styling } from "./styled.js";

/**
 * Snipcart Query String Cart Prepopulation Component
 *
 * This React component handles prepopulating the Snipcart cart from URL query parameters.
 * URL format: https://mysite.com?cart=item1:2,item2:1
 *
 * Each entry is productID:quantity where:
 * - productID: The Snipcart product ID (must match data-item-id in HTML)
 * - quantity: The number of items to add (defaults to 1 if not specified)
 *
 * Features:
 * - Waits for Snipcart to be ready before executing
 * - Parses query string parameters safely
 * - Handles edge cases (invalid format, missing products, etc.)
 * - Uses Snipcart API to add items programmatically
 * - Prevents duplicate additions on page refresh
 * - Modern React with hooks and proper error handling
 */

export default function SnipcartQueryCart({ cartParam = "cart", showDebugInfo = false, className = "" }) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [processedItems, setProcessedItems] = useState([]);
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState("");

  // Check for debug parameter in URL
  const urlParams = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new Map();
  const debugFromUrl = urlParams.get("debug") === "true";
  const shouldShowDebug = showDebugInfo || debugFromUrl;
  const forceProcess = urlParams.get("force") === "true";
  const voucherCode = urlParams.get("voucher") || urlParams.get("code") || urlParams.get("discount");

  // Configuration
  const CONFIG = {
    CART_PARAM: cartParam,
    PROCESSED_KEY: "snipcart_processed_cart",
    MAX_RETRIES: 50,
    RETRY_INTERVAL: 100,
    MAX_ITEMS: 20,
  };

  /**
   * Utility function to safely access Snipcart
   */
  const getSnipcart = () => {
    return typeof window !== "undefined" && window.Snipcart ? window.Snipcart : null;
  };

  /**
   * Get URL search parameters
   */
  const getUrlParams = () => {
    if (typeof window === "undefined" || !window.location || !window.URLSearchParams) {
      return new Map();
    }

    try {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams;
    } catch (error) {
      console.warn("Snipcart Query Cart: Failed to parse URL parameters", error);
      return new Map();
    }
  };

  /**
   * Parse cart items from query string
   */
  const parseCartItems = (cartString) => {
    if (!cartString || typeof cartString !== "string") {
      return [];
    }

    try {
      const items = cartString
        .split(",")
        .map((item) => item.trim())
        .filter((item) => item.length > 0)
        .slice(0, CONFIG.MAX_ITEMS)
        .map((item) => {
          const parts = item.split(":");
          const id = parts[0]?.trim();
          const quantity = parts[1]?.trim();

          if (!id) {
            return null;
          }

          // Parse quantity, default to 1 if invalid
          let parsedQuantity = 1;
          if (quantity) {
            const num = parseInt(quantity, 10);
            if (!isNaN(num) && num > 0 && num <= 999) {
              parsedQuantity = num;
            }
          }

          return { id, quantity: parsedQuantity };
        })
        .filter((item) => item !== null);

      return items;
    } catch (error) {
      console.warn("Snipcart Query Cart: Failed to parse cart items", error);
      return [];
    }
  };

  /**
   * Generate a unique identifier for the current cart configuration
   */
  const generateCartId = (items) => {
    if (typeof window === "undefined" || !window.btoa) {
      return items.map((item) => `${item.id}:${item.quantity}`).join(",");
    }

    try {
      const cartString = items.map((item) => `${item.id}:${item.quantity}`).join(",");
      return window.btoa(cartString);
    } catch (error) {
      return items.map((item) => `${item.id}:${item.quantity}`).join(",");
    }
  };

  /**
   * Check if this cart configuration has already been processed
   */
  const isCartProcessed = (cartId) => {
    if (typeof window === "undefined" || !window.localStorage) {
      return false;
    }

    try {
      const processed = window.localStorage.getItem(CONFIG.PROCESSED_KEY);
      return processed === cartId;
    } catch (error) {
      console.warn("Snipcart Query Cart: Failed to check processed carts", error);
      return false;
    }
  };

  /**
   * Mark cart as processed to prevent duplicate additions
   */
  const markCartProcessed = (cartId) => {
    if (typeof window === "undefined" || !window.localStorage) {
      return;
    }

    try {
      window.localStorage.setItem(CONFIG.PROCESSED_KEY, cartId);
    } catch (error) {
      console.warn("Snipcart Query Cart: Failed to mark cart as processed", error);
    }
  };

  /**
   * Find Snipcart button data for a given product ID
   */
  const findSnipcartButtonData = (productId) => {
    if (typeof window === "undefined") return null;

    const button = document.querySelector(`[data-item-id="${productId}"]`);
    if (!button) return null;

    // Extract all data attributes from the button
    const data = {};
    for (let i = 0; i < button.attributes.length; i++) {
      const attr = button.attributes[i];
      if (attr.name.startsWith("data-item-")) {
        const key = attr.name.replace("data-item-", "").replace(/-([a-z])/g, (g) => g[1].toUpperCase());
        data[key] = attr.value;
      }
    }

    console.log(`Snipcart Query Cart: Raw extracted data for ${productId}:`, data);

    // Ensure required fields are present and properly formatted
    const productData = {
      id: data.id || productId,
      name: data.name || "Unknown Product",
      price: data.price || "0.00",
      description: data.description || "",
      url: data.url || window.location.href,
      image: data.image || "",
      categories: data.categories || "",
      metadata: data.metadata || "{}",
      shippable: data.shippable === "true" || false,
      quantity: 1, // Will be overridden
    };

    // Add optional fields if they exist
    if (data.fileGuid) productData.fileGuid = data.fileGuid;
    if (data.maxQuantity) productData.maxQuantity = parseInt(data.maxQuantity, 10);
    if (data.custom1Name) productData.custom1Name = data.custom1Name;
    if (data.custom1Options) productData.custom1Options = data.custom1Options;
    if (data.custom1Value) productData.custom1Value = data.custom1Value;

    console.log(`Snipcart Query Cart: Extracted product data for ${productId}:`, productData);
    return productData;
  };

  /**
   * Apply voucher code to Snipcart
   */
  const applyVoucherCode = async (code) => {
    if (!code || typeof window === "undefined") return;

    try {
      console.log(`Snipcart Query Cart: Applying voucher code: ${code}`);

      // Wait a bit for Snipcart to be fully ready
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Click the discount button to show the input field
      const discountButton = document.querySelector(".snipcart-discount-box__button");
      if (discountButton) {
        console.log("Snipcart Query Cart: Clicking discount button to show input field");
        discountButton.click();

        // Wait for the input field to appear
        await new Promise((resolve) => setTimeout(resolve, 500));
      }

      // Find the discount code input field
      const codeInput = document.querySelector("#code");
      if (codeInput) {
        console.log("Snipcart Query Cart: Found code input field, entering voucher code");

        // Clear any existing value and enter the new code
        codeInput.value = code;

        // Trigger input event to ensure Snipcart detects the change
        codeInput.dispatchEvent(new Event("input", { bubbles: true }));
        codeInput.dispatchEvent(new Event("change", { bubbles: true }));

        // Find and click the apply button
        const applyButton = document.querySelector(".snipcart-discount-box__apply-button") || document.querySelector('button[type="submit"]') || codeInput.closest("form")?.querySelector('button[type="submit"]');

        if (applyButton) {
          console.log("Snipcart Query Cart: Clicking apply button for voucher code");
          applyButton.click();
        } else {
          // Try pressing Enter if no apply button found
          console.log("Snipcart Query Cart: Pressing Enter to apply voucher code");
          codeInput.dispatchEvent(new KeyboardEvent("keydown", { key: "Enter", bubbles: true }));
        }

        console.log(`Snipcart Query Cart: Voucher code ${code} applied successfully`);
      } else {
        console.warn("Snipcart Query Cart: Could not find code input field");
      }
    } catch (error) {
      console.warn("Snipcart Query Cart: Error applying voucher code", error);
    }
  };

  /**
   * Add items to Snipcart cart by clicking the actual buttons
   */
  const addItemsToCart = async (items) => {
    const addPromises = items.map((item) => {
      return new Promise((resolve, reject) => {
        try {
          // Find the actual Snipcart button on the page
          const button = document.querySelector(`[data-item-id="${item.id}"]`);

          if (!button) {
            const error = new Error(`Product ${item.id} not found on page`);
            console.warn(`Snipcart Query Cart: ${error.message}`);
            reject({ success: false, item, error });
            return;
          }

          console.log(`Snipcart Query Cart: Found button for ${item.id}:`, button);

          // Set the quantity on the button if it has a quantity field
          const quantityField = button.querySelector("[data-item-quantity]") || button;
          if (quantityField.hasAttribute("data-item-quantity")) {
            quantityField.setAttribute("data-item-quantity", item.quantity);
          }

          // Click the button to add to cart
          console.log(`Snipcart Query Cart: Clicking button for ${item.id}`);
          button.click();

          // Wait a bit for the click to process
          setTimeout(() => {
            console.log(`Snipcart Query Cart: Clicked button for ${item.quantity}x ${item.id}`);
            resolve({ success: true, item });
          }, 100);
        } catch (error) {
          console.warn(`Snipcart Query Cart: Error clicking button for ${item.id}`, error);
          reject({ success: false, item, error });
        }
      });
    });

    return Promise.allSettled(addPromises);
  };

  /**
   * Wait for Snipcart to be ready
   */
  const waitForSnipcart = () => {
    return new Promise((resolve, reject) => {
      let attempts = 0;

      const checkSnipcart = () => {
        const Snipcart = getSnipcart();

        if (Snipcart && Snipcart.api && Snipcart.api.cart) {
          resolve(Snipcart);
          return;
        }

        attempts++;
        if (attempts >= CONFIG.MAX_RETRIES) {
          reject(new Error("Snipcart failed to load within timeout period"));
          return;
        }

        setTimeout(checkSnipcart, CONFIG.RETRY_INTERVAL);
      };

      checkSnipcart();
    });
  };

  /**
   * Find valid Snipcart product IDs on the current page
   */
  const findValidProductIds = () => {
    if (typeof window === "undefined") return [];

    const snipcartButtons = document.querySelectorAll("[data-item-id]");
    const productIds = Array.from(snipcartButtons).map((button) => button.getAttribute("data-item-id"));

    console.log("Snipcart Query Cart: Found product IDs on page:", productIds);
    return productIds;
  };

  /**
   * Main function to process cart from query string
   */
  const processCartFromQueryString = async () => {
    try {
      setIsProcessing(true);
      setError(null);
      setProcessedItems([]);

      // Get cart parameter from URL
      const urlParams = getUrlParams();
      const cartString = urlParams.get(CONFIG.CART_PARAM);

      if (!cartString) {
        if (shouldShowDebug) {
          setDebugInfo("No cart parameter found in URL");
        }
        return;
      }

      // Parse cart items
      const items = parseCartItems(cartString);
      if (items.length === 0) {
        const message = "No valid items found in cart parameter";
        console.warn(`Snipcart Query Cart: ${message}`);
        setError(message);
        return;
      }

      // Generate unique cart ID and check if already processed
      const cartId = generateCartId(items);
      console.log(forceProcess, "forceProcess");
      console.log(cartId, "cartId");
      console.log(isCartProcessed(cartId), "isCartProcessed");

      if (!forceProcess && isCartProcessed(cartId)) {
        const message = "Cart already processed, skipping";
        console.log(`Snipcart Query Cart: ${message}`);
        if (shouldShowDebug) {
          setDebugInfo(message);
        }
        return;
      }

      console.log("Snipcart Query Cart: Processing cart items:", items);

      // Find valid product IDs if in debug mode
      if (shouldShowDebug) {
        const validIds = findValidProductIds();
        let debugMessage = `Found ${validIds.length} products on page: ${validIds.join(", ")}`;
        if (voucherCode) {
          debugMessage += ` | Voucher code: ${voucherCode}`;
        }
        setDebugInfo(debugMessage);
      }

      // Wait for Snipcart to be ready
      const Snipcart = await waitForSnipcart();
      console.log("Snipcart Query Cart: Snipcart is ready");

      // Add a small delay to ensure Snipcart is fully initialized
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Add items to cart
      const results = await addItemsToCart(items);

      // Process results
      const successful = results.filter((result) => result.status === "fulfilled");
      const failed = results.filter((result) => result.status === "rejected");

      const successfulItems = successful.map((result) => result.value.item);
      setProcessedItems(successfulItems);

      console.log(`Snipcart Query Cart: Added ${successful.length} items successfully, ${failed.length} failed`);

      // Apply voucher code if provided
      if (voucherCode && successful.length > 0) {
        console.log(`Snipcart Query Cart: Applying voucher code: ${voucherCode}`);
        await applyVoucherCode(voucherCode);
      }

      // Mark as processed only if at least one item was added successfully
      if (successful.length > 0) {
        markCartProcessed(cartId);

        // Force Snipcart to save the cart
        try {
          if (Snipcart && Snipcart.api && Snipcart.api.cart) {
            await Snipcart.api.cart.save();
            console.log("Snipcart Query Cart: Cart saved successfully");
          }
        } catch (saveError) {
          console.warn("Snipcart Query Cart: Failed to save cart", saveError);
        }
      }

      // Log any failures for debugging
      if (failed.length > 0) {
        failed.forEach((result, index) => {
          console.warn(`Snipcart Query Cart: Failed to add item ${items[index].id}:`, result.reason);
        });
        setError(`${failed.length} items failed to add to cart`);
      }

      if (shouldShowDebug) {
        setDebugInfo(`Processed ${successful.length} items successfully, ${failed.length} failed`);
      }
    } catch (error) {
      console.error("Snipcart Query Cart: Error processing cart from query string", error);
      setError(error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  // Effect to run on component mount
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    // Wait for DOM to be ready
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", processCartFromQueryString);
      return () => {
        document.removeEventListener("DOMContentLoaded", processCartFromQueryString);
      };
    } else {
      // DOM is already ready
      processCartFromQueryString();
    }
  }, []);

  // Don't render anything if not in debug mode and no errors
  if (!shouldShowDebug && !error && processedItems.length === 0) {
    return null;
  }

  return (
    <Styling className={`snipcart-query-cart ${className}`}>
      {isProcessing && (
        <div className="processing">
          <div className="spinner"></div>
          <span>Adding items to cart...</span>
        </div>
      )}

      {error && (
        <div className="error">
          <strong>Cart Error:</strong> {error}
        </div>
      )}

      {processedItems.length > 0 && (
        <div className="success">
          <strong>Added to cart:</strong>
          <ul>
            {processedItems.map((item, index) => (
              <li key={index}>
                {item.quantity}x {item.id}
              </li>
            ))}
          </ul>
        </div>
      )}

      {shouldShowDebug && debugInfo && (
        <div className="debug">
          <strong>Debug:</strong> {debugInfo}
        </div>
      )}
    </Styling>
  );
}
